// import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import HistoryIcon from "@mui/icons-material/History";
import OnlinePredictionIcon from "@mui/icons-material/OnlinePrediction";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Button, ButtonOwnProps } from "@mui/material";
import { FC, ReactNode, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAppSelector } from "../../redux";
import "./index.css";

interface ITabButtonDetails {
	key: string;
	name: string;
	icon: ReactNode;
	visible: boolean;
	color?: ButtonOwnProps["color"];
}

interface INestedPageDetails {
	path_key: string;
	children: ITabButtonDetails[];
}

const NestedPageLayout: FC = () => {
	const location = useLocation();
	const navigate = useNavigate();

	const authState = useAppSelector((state) => state.auth);

	const locationSplit = useMemo<string[]>(() => location.pathname.split("/"), [location.pathname]);
	const activeTab = useMemo(() => locationSplit[locationSplit.length - 1], [locationSplit]);

	const nestedPagesList = useMemo<INestedPageDetails[]>(() => {
		return [
			{
				path_key: "roles-permissions",
				children: [
					{
						key: "user-roles",
						name: "User Roles",
						icon: <PeopleAltIcon />,
						visible: !!authState.user.permissions && authState.user.permissions.user_management.view
					},
					{
						key: "roles-hierarchy",
						name: "Roles Hierarchy",
						icon: <PeopleAltIcon />,
						visible: !!authState.user.permissions && authState.user.permissions.user_management.view
					}
				]
			},
			{
				path_key: "user-management",
				children: [
					{
						key: "users",
						name: "Users",
						icon: <PeopleAltIcon />,
						visible: !!authState.user.permissions && authState.user.permissions.user_management.view
					},
					{
						key: "active-sessions",
						name: "Active Sessions",
						icon: <OnlinePredictionIcon />,
						visible: !!authState.user.permissions && authState.user.permissions.user_session_management.view
					}
				]
			},
			{
				path_key: "configuration",
				children: [
					{
						key: "interceptors",
						name: "Interceptors",
						icon: <DirectionsCarIcon />,
						visible: !!authState.user.permissions && authState.user.permissions.interceptor_vehicles.view
					},
					{
						key: "activity-logs",
						name: "Activity Logs",
						icon: <HistoryIcon />,
						visible: !!authState.user.permissions && authState.user.permissions.user_activity_logs.view
					},
					{
						key: "about-us",
						name: "About Us",
						icon: <InfoIcon />,
						visible: true
					}
				]
			}
		];
	}, [authState.user.permissions]);

	const tabsList = useMemo<ITabButtonDetails[]>(() => {
		const activePage = nestedPagesList.find((page) => page.path_key === locationSplit[1]);
		return activePage?.children?.filter((item) => !!item.visible) || [];
	}, [locationSplit, nestedPagesList]);

	function handleChangeTab(newTab: string) {
		navigate(`/${locationSplit[1]}/${newTab}`, { replace: true });
	}

	useEffect(() => {
		if (tabsList.length > 0 && locationSplit[2] && !tabsList.some((item) => item.key === locationSplit[2])) {
			handleChangeTab(tabsList[0]?.key);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locationSplit, tabsList]);

	return (
		<Box className="nested-page-layout-wrapper">
			{tabsList.length > 1 && tabsList.findIndex((tabItem) => tabItem.key === activeTab) >= 0 ? (
				<Box className="nested-page-layout-header">
					{tabsList.map((tabItem) => (
						<Button
							key={tabItem.key}
							disableElevation
							variant={activeTab === tabItem.key ? "contained" : "outlined"}
							color={tabItem.color ? tabItem.color : "primary"}
							startIcon={tabItem.icon}
							onClick={() => handleChangeTab(tabItem.key)}
							size="large"
						>
							{tabItem.name}
						</Button>
					))}
				</Box>
			) : null}

			<Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", height: 0 }}>
				<Outlet />
			</Box>
		</Box>
	);
};

export default NestedPageLayout;
